import { authStore } from '@/core/auth/authStore';
import { env } from '@/core/config/env';
import type { TokenPairResponse } from '@/core/types/auth';
import { ApiError, type ProblemDetails } from '@/core/utils/errors';

export interface ApiResponse<T> {
  data: T;
  status: number;
  headers: Headers;
}

interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  authenticated?: boolean;
  retryOnUnauthorized?: boolean;
  timeoutMs?: number;
}

let refreshPromise: Promise<TokenPairResponse> | null = null;

const toUrl = (path: string): string => `${env.apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`;

const parseResponse = async <T>(response: Response): Promise<T> => {
  if (response.status === 204) return undefined as T;
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json') || contentType.includes('problem+json')) {
    return (await response.json()) as T;
  }
  return (await response.text()) as T;
};

const buildHeaders = (options: ApiRequestOptions): Headers => {
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');

  if (options.body !== undefined && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  if (options.authenticated !== false) {
    const token = authStore.getState().accessToken;
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }

  return headers;
};

const execute = async <T>(path: string, options: ApiRequestOptions): Promise<ApiResponse<T>> => {
  const controller = new AbortController();
  const timeout = window.setTimeout(
    () => controller.abort(),
    options.timeoutMs ?? env.requestTimeoutMs,
  );

  try {
    const response = await fetch(toUrl(path), {
      ...options,
      body:
        options.body === undefined
          ? undefined
          : options.body instanceof FormData
            ? options.body
            : JSON.stringify(options.body),
      headers: buildHeaders(options),
      signal: controller.signal,
    });

    const data = await parseResponse<T | ProblemDetails>(response);
    if (!response.ok) {
      const problem = typeof data === 'object' && data !== null ? (data as ProblemDetails) : undefined;
      const message =
        problem?.detail || problem?.title || `درخواست با کد ${response.status} ناموفق بود.`;
      throw new ApiError(
        message,
        response.status,
        problem,
        response.headers.get('X-Correlation-ID') || undefined,
      );
    }

    return { data: data as T, status: response.status, headers: response.headers };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError('زمان پاسخ‌گویی سرویس به پایان رسید.', 408);
    }
    if (error instanceof TypeError) {
      throw new ApiError('ارتباط با سرویس برقرار نشد. اتصال شبکه و آدرس API را بررسی کنید.', 0);
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
};

const refreshAccessToken = async (): Promise<TokenPairResponse> => {
  if (refreshPromise) return refreshPromise;

  const refreshToken = authStore.getState().refreshToken;
  if (!refreshToken) throw new ApiError('نشست کاربر منقضی شده است.', 401);

  refreshPromise = execute<TokenPairResponse>('/api/v1/auth/token/refresh', {
    method: 'POST',
    body: { refreshToken },
    authenticated: false,
    retryOnUnauthorized: false,
  })
    .then((result) => {
      authStore.getState().setTokens(result.data);
      return result.data;
    })
    .catch((error) => {
      authStore.getState().clearSession();
      throw error;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

export const httpClient = {
  async request<T>(path: string, options: ApiRequestOptions = {}): Promise<ApiResponse<T>> {
    try {
      return await execute<T>(path, options);
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.status === 401 &&
        options.authenticated !== false &&
        options.retryOnUnauthorized !== false
      ) {
        await refreshAccessToken();
        return execute<T>(path, { ...options, retryOnUnauthorized: false });
      }
      throw error;
    }
  },

  get<T>(path: string, options: ApiRequestOptions = {}): Promise<ApiResponse<T>> {
    return this.request<T>(path, { ...options, method: 'GET' });
  },

  post<T>(path: string, body?: unknown, options: ApiRequestOptions = {}): Promise<ApiResponse<T>> {
    return this.request<T>(path, { ...options, method: 'POST', body });
  },

  patch<T>(path: string, body?: unknown, options: ApiRequestOptions = {}): Promise<ApiResponse<T>> {
    return this.request<T>(path, { ...options, method: 'PATCH', body });
  },

  put<T>(path: string, body?: unknown, options: ApiRequestOptions = {}): Promise<ApiResponse<T>> {
    return this.request<T>(path, { ...options, method: 'PUT', body });
  },

  async download(path: string, filename: string, retryOnUnauthorized = true): Promise<void> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), env.requestTimeoutMs * 3);
    try {
      const token = authStore.getState().accessToken;
      const headers = new Headers({ Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      if (token) headers.set('Authorization', `Bearer ${token}`);
      const response = await fetch(toUrl(path), { method: 'GET', headers, signal: controller.signal });
      if (response.status === 401 && retryOnUnauthorized) {
        await refreshAccessToken();
        return this.download(path, filename, false);
      }
      if (!response.ok) {
        let message = `درخواست با کد ${response.status} ناموفق بود.`;
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('json')) {
          const problem = (await response.json()) as ProblemDetails;
          message = problem.detail || problem.title || message;
        }
        throw new ApiError(message, response.status);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      try {
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw new ApiError('زمان دریافت فایل به پایان رسید.', 408);
      throw error;
    } finally {
      window.clearTimeout(timeout);
    }
  },
};
